import { captureEvent } from "@sentry/react";
import {
  BudgetGroupsForProjectsDocument,
  type BudgetGroupsForProjectsQuery,
  type BudgetGroupsForProjectsQueryVariables,
  CreateQuoteDocument,
  type CreateQuoteMutation,
  type CreateQuoteMutationVariables,
  ProjectTitleDocument,
  type ProjectTitleQuery,
  type ProjectTitleQueryVariables,
} from "@src/__generated__/graphql";
import { BACK_BUTTON_QUERY_PARAM_KEY } from "@src/components/ui-kit/NavBar/BackButton";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { action, computed, makeObservable, observable } from "mobx";
import { BudgetGroupModel } from "./models/budget-group";

export class BudgetStore {
  readonly tableKey = "budget-group";
  budgetGroupsFetcher = new FetchHelper<
    BudgetGroupsForProjectsQuery,
    BudgetGroupsForProjectsQueryVariables
  >(BudgetGroupsForProjectsDocument);
  projectTitleFetcher = new FetchHelper<
    ProjectTitleQuery,
    ProjectTitleQueryVariables
  >(ProjectTitleDocument);
  createQuoteMutator = new MutationHelper<
    CreateQuoteMutation,
    CreateQuoteMutationVariables
  >(CreateQuoteDocument);

  @observable.ref projectInfo: NonNullable<
    ProjectTitleQuery["project"]
  > | null = null;
  @observable budgetGroups: BudgetGroupModel[] = [];

  constructor(private appStore: AppStore) {
    makeObservable(this);
  }

  @computed get backButtonParam() {
    return location.pathname + location.search;
  }

  @computed private get allSelectedIds() {
    return this.budgetGroups.flatMap((group) => group.selectedIds);
  }

  @computed get isDownloadReportButtonDisabled() {
    return this.allSelectedIds.length === 0;
  }

  @action.bound handleDownloadReport() {
    this.appStore.budgetsExportStore.exportQuotes(this.allSelectedIds);
  }

  @action async fetchProjectInfo(projectId: string) {
    const [data, error] = await this.projectTitleFetcher.fetch({ projectId });
    if (error) return;
    this.projectInfo = data.project ?? null;
  }

  @action.bound async fetchBudgetGroups(projectId: string) {
    const [data, error] = await this.budgetGroupsFetcher.fetch({ projectId });

    if (error || !data.budgetsForProject) {
      this.budgetGroups = [];
      return;
    }

    this.budgetGroups = data.budgetsForProject
      .toSorted((a, b) => (a < b ? 1 : -1))
      .map((group) => new BudgetGroupModel(group));
  }

  @action.bound handleAssignInGroup(id: string) {
    const group = this.budgetGroups.find((group) =>
      group.budgets.find((budget) => budget.id === id),
    );

    if (!group) {
      captureEvent({
        message: "FE: Unable to find budget to assign",
      });
      this.appStore.UIStore.toast({
        status: "error",
      });
      return;
    }

    group.budgets.forEach((budget) => {
      budget.is_active = false;
    });

    group.budgets.find((budget) => budget.id === id)?.handleAssign();
  }

  @action.bound async handleDeleteInGroup(id: string) {
    const index = this.budgetGroups.findIndex((group) =>
      group.budgets.find((budget) => budget.id === id),
    );
    if (index === -1) {
      captureEvent({
        message: "FE: Unable to find budget group for budget removal.",
      });
      this.appStore.UIStore.toast({
        status: "error",
      });
      return;
    }

    const group = this.budgetGroups[index];
    await group.handleBudgetDelete(id);
    if (group.budgets.length > 0) return;
    this.budgetGroups.splice(index, 1);
  }

  @action.bound async handleCreateQuote() {
    const [data, error] = await this.createQuoteMutator.mutate({
      budget_ids: this.allSelectedIds,
    });

    if (error) {
      this.appStore.UIStore.toast({ status: "error" });
      return;
    }

    window.open(
      `/budgets/edit-quote?id=${data.createQuote.id}&${BACK_BUTTON_QUERY_PARAM_KEY}=${location.pathname}${location.search}`,
      "_blank",
    );
  }
}
